// ---------------------------------------------------------------------------
// Course Component: Progress Bar — Visual Completion
// ---------------------------------------------------------------------------

import { registerRenderer } from "../registry.js";
import type { RenderContext } from "../types.js";
import { attr, text } from "../escape.js";
import { sanitizeThemeTokenColor } from "../css-cascade.js";

export interface ProgressBarProps {
  /** What to track: module completion, course completion, or custom */
  trackingScope: "lesson" | "module" | "course" | "custom";
  /** Current progress 0-100 (injected at render time from enrollment data) */
  currentPercent?: number;
  /** Show percentage text */
  showPercent?: boolean;
  /** Color when complete */
  completeColor?: string;
  /** Color for in-progress */
  progressColor?: string;
  label?: string;
}

/**
 * Compute progress percentage from completed/total items.
 */
export function computeProgress(completed: number, total: number): number {
  if (total <= 0) return 0;
  return Math.min(100, Math.round((completed / total) * 100));
}

export const PROGRESS_BAR_DEF = {
  type: "progress-bar" as const,
  label: "Progress Bar",
  category: "course",
  defaultProps: {
    trackingScope: "module" as const,
    showPercent: true,
    progressColor: "var(--ease-color-brand,#3b82f6)",
    completeColor: "var(--ease-color-success,#22c55e)",
    label: "Your Progress",
  } satisfies Partial<ProgressBarProps>,
};

function render(props: Record<string, unknown>, _ctx: RenderContext): string {
  const p = props as unknown as ProgressBarProps;
  const currentPercent = Math.max(0, Math.min(100, p.currentPercent ?? 0));
  const isComplete = currentPercent >= 100;
  const fallbackColor = isComplete
    ? "var(--ease-color-success,#22c55e)"
    : "var(--ease-color-brand,#3b82f6)";
  const progressColor = sanitizeThemeTokenColor(
    isComplete ? (p.completeColor ?? fallbackColor) : (p.progressColor ?? fallbackColor),
  ) ?? fallbackColor;

  return `<section class="ease-section ease-course-progress" data-tracking-scope="${attr(p.trackingScope ?? "course")}">
  <div class="ease-course-progress__header">
    <span class="ease-course-progress__label">${text(p.label ?? "Your Progress")}</span>
    ${p.showPercent !== false ? `<span class="ease-course-progress__value">${text(String(currentPercent))}%</span>` : ""}
  </div>
  <div class="ease-course-progress__track" aria-label="${attr(`${currentPercent}% complete`)}" style="background:var(--ease-color-surface-muted,var(--ease-color-surface,#f3f4f6));border-radius:999px;overflow:hidden">
    <div class="ease-course-progress__fill" style="width: ${attr(String(currentPercent))}%; background: ${attr(progressColor)};"></div>
  </div>
</section>`;
}

registerRenderer("progress-bar", { render });
